import React, { useState, useId, useCallback } from 'react';
import { Shield, User, Phone, Mail, Link2, ChevronDown, ChevronUp, ExternalLink, Loader2, Briefcase } from 'lucide-react';
import useModal from '../../hooks/useModal';
import { personService, clientService } from '../../services/api';

const ROLE_LABELS = {
  client: 'Client',
  opposing_party: 'Opposing Party',
  opposing_counsel: 'Opposing Counsel',
  co_counsel: 'Co-Counsel',
  witness: 'Witness',
  expert_witness: 'Expert Witness',
  judge: 'Judge',
  guardian_ad_litem: 'Guardian ad Litem',
  mediator: 'Mediator',
  insurer: 'Insurer',
  third_party: 'Third Party',
};

const MATCH_TYPE_LABELS = {
  exact_email: 'Email Match',
  exact_phone: 'Phone Match',
  exact_name: 'Name Match',
  exact_company: 'Company Match',
  fuzzy_name: 'Similar Name',
  fuzzy_company: 'Similar Company',
  relationship: 'Related Person',
};

const MATCH_TYPE_COLORS = {
  exact_email: 'bg-red-100 text-red-800',
  exact_phone: 'bg-red-100 text-red-800',
  exact_name: 'bg-orange-100 text-orange-800',
  exact_company: 'bg-orange-100 text-orange-800',
  fuzzy_name: 'bg-yellow-100 text-yellow-800',
  fuzzy_company: 'bg-yellow-100 text-yellow-800',
  relationship: 'bg-purple-100 text-purple-800',
};

const CONTACT_TYPE_LABELS = {
  email: 'Email',
  phone_cell: 'Cell',
  phone_home: 'Home',
  phone_work: 'Work',
  fax: 'Fax',
  linkedin: 'LinkedIn',
  facebook: 'Facebook',
  twitter: 'Twitter',
  instagram: 'Instagram',
  website: 'Website',
  other_social: 'Other',
};

const ConflictWarningDialog = ({
  isOpen,
  onClose,
  onOverride,
  conflicts = [],
  conflictCheckId,
  loading = false,
}) => {
  const [overrideReason, setOverrideReason] = useState('');
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [detailsCache, setDetailsCache] = useState({});
  const [detailsLoading, setDetailsLoading] = useState({});
  const { handleBackdropMouseDown, handleBackdropMouseUp } = useModal(isOpen, onClose);
  const titleId = useId();
  const descId = useId();

  const fetchDetails = useCallback(async (match) => {
    const entityId = match.matched_entity_id;
    if (detailsCache[entityId] || detailsLoading[entityId]) return;

    setDetailsLoading(prev => ({ ...prev, [entityId]: true }));
    try {
      if (match.matched_entity_type === 'person') {
        const res = await personService.get(entityId);
        setDetailsCache(prev => ({ ...prev, [entityId]: { type: 'person', data: res.data } }));
      } else if (match.matched_entity_type === 'client') {
        const res = await clientService.getClient(entityId);
        setDetailsCache(prev => ({ ...prev, [entityId]: { type: 'client', data: res.data } }));
      }
    } catch {
      setDetailsCache(prev => ({ ...prev, [entityId]: { type: match.matched_entity_type, data: null, error: true } }));
    } finally {
      setDetailsLoading(prev => ({ ...prev, [entityId]: false }));
    }
  }, [detailsCache, detailsLoading]);

  const handleToggleExpand = useCallback((index, match) => {
    if (expandedIndex === index) {
      setExpandedIndex(null);
    } else {
      setExpandedIndex(index);
      fetchDetails(match);
    }
  }, [expandedIndex, fetchDetails]);

  if (!isOpen) return null;

  const handleOverride = () => {
    if (overrideReason.trim() && onOverride) {
      onOverride(conflictCheckId, overrideReason.trim());
    }
  };

  const getConfidenceColor = (confidence) => {
    const val = parseFloat(confidence);
    if (val >= 0.90) return 'text-red-600';
    if (val >= 0.70) return 'text-orange-600';
    return 'text-yellow-600';
  };

  const getConfidenceLabel = (confidence) => {
    const val = parseFloat(confidence);
    if (val >= 0.90) return 'Very High';
    if (val >= 0.70) return 'High';
    if (val >= 0.50) return 'Moderate';
    return 'Low';
  };

  const renderPersonDetails = (data) => {
    if (!data) return <p className="text-sm text-gray-500 italic">Failed to load details</p>;

    return (
      <div className="space-y-3">
        {/* Contacts */}
        {data.contacts && data.contacts.length > 0 && (
          <div>
            <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Contacts</h5>
            <div className="space-y-1">
              {data.contacts.map((c) => (
                <div key={c.id} className="flex items-center gap-2 text-sm text-gray-700">
                  {c.contact_type === 'email' ? (
                    <Mail className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                  ) : c.contact_type.startsWith('phone') ? (
                    <Phone className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                  ) : (
                    <Link2 className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                  )}
                  <span className="text-gray-500 text-xs">{CONTACT_TYPE_LABELS[c.contact_type] || c.contact_type}:</span>
                  <span>{c.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Case Appearances */}
        {data.case_parties && data.case_parties.length > 0 && (
          <div>
            <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Case Appearances</h5>
            <div className="space-y-1">
              {data.case_parties.map((cp) => (
                <div key={cp.id} className="flex items-center gap-2 text-sm text-gray-700">
                  <Briefcase className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                  <span>
                    {cp.case_number && <span className="font-medium">{cp.case_number}</span>}
                    {cp.case_title && <span className="text-gray-500">{cp.case_number ? ': ' : ''}{cp.case_title}</span>}
                    <span className="text-gray-400 ml-1">({ROLE_LABELS[cp.role] || cp.role})</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Relationships */}
        {data.relationships && data.relationships.length > 0 && (
          <div>
            <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Relationships</h5>
            <div className="space-y-1">
              {data.relationships.map((rel) => (
                <div key={rel.id} className="flex items-center gap-2 text-sm text-gray-700">
                  <Link2 className="h-3.5 w-3.5 text-purple-400 flex-shrink-0" />
                  <span className="text-gray-500 text-xs">{rel.relationship_type.replace(/_/g, ' ')}:</span>
                  <span>{rel.related_person_name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Open Full Profile link */}
        <a
          href={`/persons/${data.id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 font-medium mt-1"
        >
          Open Full Profile
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
    );
  };

  const renderClientDetails = (data) => {
    if (!data) return <p className="text-sm text-gray-500 italic">Failed to load details</p>;

    return (
      <div className="space-y-3">
        {/* Contact info */}
        <div>
          <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Contact Info</h5>
          <div className="space-y-1">
            {data.email && (
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <Mail className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                <span>{data.email}</span>
              </div>
            )}
            {data.phone && (
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <Phone className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                <span>{data.phone}</span>
              </div>
            )}
            {data.address && (
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <span className="text-gray-500 text-xs">Address:</span>
                <span>{data.address}</span>
              </div>
            )}
          </div>
        </div>

        {/* Notes */}
        {data.notes && (
          <div>
            <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Notes</h5>
            <p className="text-sm text-gray-700">{data.notes}</p>
          </div>
        )}

        {/* Open Full Profile link */}
        <a
          href={`/clients/${data.id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 font-medium mt-1"
        >
          Open Full Profile
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
    );
  };

  const renderExpandedDetails = (match) => {
    const entityId = match.matched_entity_id;
    const isLoading = detailsLoading[entityId];
    const cached = detailsCache[entityId];

    if (isLoading) {
      return (
        <div className="flex items-center gap-2 py-3 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading details...
        </div>
      );
    }

    if (!cached) return null;

    if (cached.type === 'person') {
      return renderPersonDetails(cached.data);
    } else if (cached.type === 'client') {
      return renderClientDetails(cached.data);
    }

    return null;
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-[9999]"
      onMouseDown={handleBackdropMouseDown}
      onMouseUp={handleBackdropMouseUp}
    >
      <div className="fixed inset-x-0 top-16 bottom-0 bg-black bg-opacity-60 -z-10" aria-hidden="true"></div>
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[80vh] flex flex-col"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 pb-4 border-b flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center h-10 w-10 rounded-full bg-red-100">
              <Shield className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <h3 id={titleId} className="text-lg font-semibold text-gray-900">
                Conflict of Interest Detected
              </h3>
              <p id={descId} className="text-sm text-gray-500">
                {conflicts.length} potential conflict{conflicts.length !== 1 ? 's' : ''} found
              </p>
            </div>
          </div>
        </div>

        {/* Conflict List */}
        <div className="overflow-y-auto flex-1 p-6 space-y-3">
          {conflicts.map((match, index) => (
            <div key={index} className="border border-gray-200 rounded-lg bg-gray-50 overflow-hidden">
              <button
                type="button"
                onClick={() => handleToggleExpand(index, match)}
                className="w-full text-left p-4 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {match.matched_entity_type === 'client' ? (
                      <User className="h-4 w-4 text-blue-600" />
                    ) : (
                      <User className="h-4 w-4 text-gray-600" />
                    )}
                    <span className="font-medium text-gray-900">
                      {[match.matched_first_name, match.matched_last_name].filter(Boolean).join(' ')}
                      {match.matched_company_name && (
                        <span className="text-gray-500 ml-1">({match.matched_company_name})</span>
                      )}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${MATCH_TYPE_COLORS[match.match_type] || 'bg-gray-100 text-gray-800'}`}>
                      {MATCH_TYPE_LABELS[match.match_type] || match.match_type}
                    </span>
                    {expandedIndex === index ? (
                      <ChevronUp className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    )}
                  </div>
                </div>

                {match.matched_contact_value && (
                  <div className="flex items-center gap-1.5 text-sm text-gray-600 mb-1">
                    {match.match_type === 'exact_email' ? (
                      <Mail className="h-3.5 w-3.5" />
                    ) : match.match_type === 'exact_phone' ? (
                      <Phone className="h-3.5 w-3.5" />
                    ) : null}
                    <span>{match.matched_contact_value}</span>
                  </div>
                )}

                {match.relationship_info && (
                  <div className="flex items-center gap-1.5 text-sm text-purple-700 mb-1">
                    <Link2 className="h-3.5 w-3.5" />
                    <span>{match.relationship_info}</span>
                  </div>
                )}

                <div className="flex items-center justify-between mt-2 text-sm">
                  <div className="text-gray-600">
                    {match.case_number && (
                      <span>Case {match.case_number}{match.case_title ? `: ${match.case_title}` : ''}</span>
                    )}
                    {match.role_on_case && (
                      <span className="ml-2 text-gray-500">
                        ({ROLE_LABELS[match.role_on_case] || match.role_on_case})
                      </span>
                    )}
                  </div>
                  <span className={`font-medium ${getConfidenceColor(match.confidence)}`}>
                    {getConfidenceLabel(match.confidence)} ({Math.round(parseFloat(match.confidence) * 100)}%)
                  </span>
                </div>
              </button>

              {/* Expanded Details */}
              {expandedIndex === index && (
                <div className="px-4 pb-4 pt-2 border-t border-gray-200 bg-gray-100/50 ml-4">
                  {renderExpandedDetails(match)}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Override Section */}
        <div className="p-6 pt-4 border-t flex-shrink-0 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Override Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              placeholder="Explain why this conflict is not a concern (e.g., confirmed with supervising attorney)..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 resize-none"
              rows={2}
            />
          </div>

          <div className="flex space-x-3">
            <button
              onClick={onClose}
              disabled={loading}
              className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 disabled:opacity-50 transition-colors text-sm font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleOverride}
              disabled={loading || !overrideReason.trim()}
              className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors text-sm font-medium"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Processing...
                </div>
              ) : (
                'Acknowledge & Proceed'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConflictWarningDialog;
